import type { BuildingCategory, PoiContent } from '@/services/dto';
import { useFnbReviewList } from '@/services/queries';
import { useConfigStore } from '@/stores';
import type { FacilityPoiDetailProps } from '@/types/facility';
import type { PoiContentItem } from '@/types/floor-guide';
import type { MenuItem, PoiDetails, ReviewItem } from '@/types/shop';
import { generateMenuKey } from '@/utils/floor-guide';
import { isDutyFreePoi, getPoiBuildingName, shouldShowBuildingName } from '@/utils/shop';
import { SHOP_CATEGORIES } from '@/constants/shop';
import type { IFloor } from 'dabeeomaps/dist/src/indexAPI';
import type { IDataFloor } from 'dabeeomaps/dist/src/model/map/IDataFloor';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePoiDetail } from './usePoiDetail';

export const usePoiContentModalState = (selectedPoiId?: string, selectedFloor?: IDataFloor | IFloor | null) => {
    const { handleGetPoiListInAllBuildings, handleGetPoiItemInAllBuildings } = usePoiDetail();

    const server = useConfigStore((state) => state.config?.server);
    const language = useConfigStore((state) => state.config?.ui?.language);
    const buildings = useConfigStore((state) => state.config?.server?.store?.buildings);

    const [selectedPoiContent, setSelectedPoiContent] = useState<PoiContent | undefined>(() => handleGetPoiItemInAllBuildings(selectedPoiId));

    const relatedPoiContentList = useMemo(() => handleGetPoiListInAllBuildings(selectedPoiId), [selectedPoiId, handleGetPoiListInAllBuildings]);

    const { data, isLoading } = useFnbReviewList(
        selectedPoiContent?.contents?.cstrShopNo?.[language] || selectedPoiContent?.contents?.cstrShopNo?.['ko'] || ''
    );

    const storeCode = server?.store?.storeCode ?? null;

    const isShowBuilding = useMemo(
        () => shouldShowBuildingName(server?.store?.buildings, storeCode, language),
        [server?.store?.buildings, language, storeCode]
    );

    const selectedBuildingName = useMemo(() => {
        if (!selectedPoiContent || !Array.isArray(buildings)) {
            return '';
        }
        const building = buildings.find((building) => building.studioBuildingId === selectedPoiContent.studioBuildingId);
        const defaultName = (building?.name?.[language] ? building?.name?.[language] : building?.name?.['ko']) || '';
        return getPoiBuildingName(selectedPoiContent, language, defaultName, storeCode);
    }, [selectedPoiContent, buildings, language, storeCode]);

    const shopCategories = useMemo(() => {
        if (!Array.isArray(buildings)) {
            return [];
        }

        return buildings.flatMap((building) => (Array.isArray(building.buildingCategories) ? building.buildingCategories : []));
    }, [buildings]);

    const fnbCategories = useMemo(() => {
        if (!Array.isArray(buildings)) {
            return [];
        }

        return buildings.flatMap((building) => (Array.isArray(building.buildingCategories) ? building.buildingCategories : []));
    }, [buildings]);

    const getDutyFreeCategoryName = useCallback(() => {
        if (!selectedPoiContent) return '';
        const lrclsCd = selectedPoiContent.contents?.lrclsCd;
        const categoryCode = lrclsCd ? lrclsCd[language] || lrclsCd['ko'] || '' : '';
        return SHOP_CATEGORIES[categoryCode] ?? '';
    }, [selectedPoiContent, language]);

    const getSelectedCategoryName = useCallback(
        (categoryList: BuildingCategory[]) => {
            if (selectedPoiContent && !!getDutyFreeCategoryName()) {
                return getDutyFreeCategoryName();
            }

            if (!Array.isArray(categoryList) || !categoryList?.length) {
                return '';
            }

            const category = categoryList.find((category) => selectedPoiContent?.categories.includes(category.categoryCode));

            return category?.name?.[language] || category?.name?.['ko'] || '';
        },
        [selectedPoiContent, language, getDutyFreeCategoryName]
    );

    const floorName = useMemo(() => {
        const name = selectedFloor?.name;

        if (!Array.isArray(name) || !name?.length) {
            return '';
        }

        return name.find((item) => item.lang === language)?.text || name.find((item) => item.lang === 'ko')?.text || '';
    }, [selectedFloor, language]);

    const parseMenuList = (rawMenuList: string | undefined): MenuItem[] => {
        if (!rawMenuList) {
            return [];
        }

        try {
            const parsed = JSON.parse(rawMenuList);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    const fnbReviewList = useMemo((): ReviewItem[] => {
        if (!Array.isArray(data?.data?.reviewInfoList)) {
            return [];
        }

        return data?.data?.reviewInfoList?.map((review) => {
            const imageSrc = Array.isArray(review?.imgUrlList) && review.imgUrlList?.length ? review.imgUrlList[0] : '';

            return {
                id: review.prdcEvlNo || '',
                imageSrc: imageSrc,
                content: review.revwCont,
                reviewedDate: review.rgstDtm,
                rating: Number(review.prdcEvlScor),
                shopName: selectedPoiContent?.name?.[language] || selectedPoiContent?.name?.['ko'] || '',
            };
        });
    }, [data?.data?.reviewInfoList, selectedPoiContent?.name, language]);

    const getLangValue = (obj: any, lang: string) => {
        return obj?.[lang] || obj?.['ko'] || '';
    };

    const facilityPoiState = useMemo<FacilityPoiDetailProps>(() => {
        return {
            poiName: getLangValue(selectedPoiContent?.name, language),
            poiImage: getLangValue(selectedPoiContent?.contents?.shopTmrkImgUrlApp, language),
            phoneNumber: getLangValue(selectedPoiContent?.contents?.shopCntc, language),
            buildingAddress: selectedBuildingName,
            floorName,
            description: getLangValue(selectedPoiContent?.contents?.shopIntrdn, language),
            showBuildingAddress: isShowBuilding,
        };
    }, [selectedPoiContent, language, isShowBuilding, selectedBuildingName, floorName]);

    const shopPoiState = useMemo<PoiContentItem>(() => {
        return {
            id: selectedPoiContent?.id,
            name: getLangValue(selectedPoiContent?.name, language),
            address: selectedBuildingName,
            floorName: floorName,
            category: getSelectedCategoryName(shopCategories),
            phoneNumber: getLangValue(selectedPoiContent?.contents?.shopCntc, language),
            imageSrc: getLangValue(selectedPoiContent?.contents?.shopTmrkImgUrlApp, language),
            showBuildingAddress: isShowBuilding,
        };
    }, [selectedPoiContent, language, floorName, isShowBuilding, selectedBuildingName, shopCategories, getSelectedCategoryName]);

    const fnbPoiState = useMemo<PoiDetails>(() => {
        return {
            name: getLangValue(selectedPoiContent?.name, language),
            imageSrc: getLangValue(selectedPoiContent?.contents?.shopTmrkImgUrlApp, language),
            address: selectedBuildingName,
            floorName: floorName,
            phoneNumber: getLangValue(selectedPoiContent?.contents?.shopCntc, language),
            category: getSelectedCategoryName(fnbCategories),
        };
    }, [selectedPoiContent, language, floorName, selectedBuildingName, fnbCategories, getSelectedCategoryName]);

    const fnbMenuList = useMemo((): MenuItem[] => {
        const menu = selectedPoiContent?.contents?.menuList;
        const menuList = parseMenuList(menu?.[language] ? menu?.[language] : menu?.['ko']);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return menuList.map((menu: any) => ({
            id: menu?.shopPrdcNo,
            name: menu?.[generateMenuKey(language)],
            price: menu?.prdcPrc,
        }));
    }, [selectedPoiContent?.contents?.menuList, language]);

    useEffect(() => {
        setSelectedPoiContent(handleGetPoiItemInAllBuildings(selectedPoiId));
    }, [selectedPoiId, handleGetPoiItemInAllBuildings]);

    return {
        facilityPoiState,
        shopPoiState,
        fnbPoiState,
        fnbReviewList,
        loadingFnb: isLoading,
        fnbMenuList,
        relatedPoiContentList,
        setSelectedPoiContent,
    };
};
